import React, { useEffect, useState } from "react";
import { Button, Col, Modal, Row, Tabs, Tab } from "react-bootstrap";
import styles from "./ProductPop.module.scss";
import Select from "react-select";
import Dropzone from "../../Dropzone/Dropzone";
import ProductVariationDropzone from "../../Dropzone/ProductVariationDropzone";
import CKEditorNew from "../../newCkEditor/ckEditor";
import { CATEGORY_TYPE, Variation_TYPE } from "../../../constants/enum";
import {
  useAddProductMutation,
  useGetProductsQuery,
  useUpdateProductMutation,
} from "../../../store/services/ProductStore";
import { useGetCategoryQuery } from "../../../store/services/categoryService";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useGetDietaryQuery } from "../../../store/services/DietaryPreferences";
import { useGetSubCategoryQuery } from "../../../store/services/categoryService";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  showToastOnce,
  showSuccessToast,
  createInitialFlavour,
  createInitialFlavourInput,
  createInitialSize,
  updateNutritionalField,
  addToArray,
  removeFromArray,
  prepareFlavourData,
} from "../../Common/ProductUtils";

const initialValues = {
  name: "",
  category: "",
  serving: "",
  variationType: 1,
  subCategory: "",
  description: "",
  flavours: [createInitialFlavour()],
};
const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Product name is required")
    .max(40, "Name cannot be more than 40 characters"),
  category: Yup.string().required("Category is required"),
  serving: Yup.string()
    .required("Serving cannot be empty")
    .max(40, "Serving cannot be more than 40 characters"),
  flavours: Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .required("Name is required")
        .max(40, "Flavour name cannot be more than 40 characters"),
      images: Yup.array()
        .of(Yup.string().required())
        .min(1, "At least one image is required "),
      sizes: Yup.array()
        .of(
          Yup.object().shape({
            name: Yup.string()
              .required("Size name is required")
              .max(40, "Size name cannot be more than 40 characters"),
            images: Yup.array()
              .of(Yup.string().required())
              .min(1, "At least one image is required for each size"),
            price: Yup.string()
              .required("price  is required")
              // .nullable(true)

              .matches(/^(\d{1,10})(\.\d{1,2})?$/, "Max 10 digits"),
            discountedPrice: Yup.string()
              .nullable(true)

              .matches(/^(\d{1,10})(\.\d{1,2})?$/, "Max 10 digits,")
              .test(
                "discount-lte-price",
                "Discounted price cannot be greater than price",
                function (value) {
                  const { price } = this.parent;
                  if (!price || !value) return true;
                  return parseFloat(value) <= parseFloat(price);
                }
              ),
            quantity: Yup.number().nullable(true).notRequired(),
          })
        )
        .min(1),
    })
  ),
  nutritionalInformation: Yup.object().shape({
    calories: Yup.number().typeError("Calories must be a number"),
    carbohydrates: Yup.number().typeError("Carbohydrates must be a number"),
    proteins: Yup.number().typeError("Proteins must be a number"),
    fats: Yup.number().typeError("Fats must be a number"),
  }),
});
const ProductPop = ({ modalOpen, setModalOpen, title, edit }) => {
  const { data: categoriesData, isLoading: isCategoriesLoading } =
    useGetCategoryQuery({
      type: CATEGORY_TYPE.PRODUCT,
      limit: 100,
      pageNo: 1,
      orderBy: "createdAt",
    });

  const { data: dietaryData, isLoading: isDietaryLoading } = useGetDietaryQuery(
    { limit: 100, pageNo: 1, orderBy: "createdAt", order: -1 }
  );

  const [selectedCategory, setSelectedCategory] = useState(null);
  const { data: subCategoriesData, isLoading: isSubCategoriesLoading } =
    useGetSubCategoryQuery(
      selectedCategory
        ? {
            type: CATEGORY_TYPE.PRODUCT,
            parentCategory: selectedCategory.value,
            limit: 100,
            orderBy: "createdAt",
            pageNo: 1,
          }
        : skipToken
    );

  useEffect(() => {
    if (modalOpen && edit) {
      formik.setValues({ ...edit, variationType: String(edit.variationType) });
      setFlavourInputs(edit?.flavours?.map((_) => createInitialFlavourInput()));
      setTimeout(() => {
        setSelectedCategory({
          value: edit?.category?._id,
          label: edit?.category?.name,
        });
        formik.setFieldValue("category", edit?.category?._id);
      }, 100);
    }
    // Only run when modal is opened or edit changes
  }, [modalOpen, edit]);
  const [addProduct, { isLoading: isAdding }] = useAddProductMutation();
  const [updateProduct, { isLoading: isupdating }] = useUpdateProductMutation();

  useEffect(() => {
    if (edit && subCategoriesData?.status_code) {
      formik.setFieldValue("subCategory", edit?.subCategory?._id);
    }
  }, [subCategoriesData]);
  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      const missingStockStatus = (formik.values.flavours || []).some(
        (flavour) => !flavour.stockStatus
      );
      if (missingStockStatus) {
        showToastOnce("Please select Stock Status for all flavours");
        setSubmitting(false);
        return;
      }
      try {
        let updatedData = {};
        const flavours = formik.values.flavours.map((f) =>
          prepareFlavourData(f, !!(edit && edit._id))
        );
        updatedData = {
          name: formik.values.name,
          category: formik.values.category,
          variationType: formik.values.variationType,
          description: formik.values.description,
          flavours,
        };
        if (formik.values.serving) {
          updatedData.serving = formik.values.serving;
        }
        if (formik.values.subCategory) {
          updatedData.subCategory = formik.values.subCategory;
        }
        if (edit && edit._id) {
          await updateProduct({
            productId: edit._id,
            updatedData: updatedData,
          }).unwrap();
          showSuccessToast("Product updated successfully");
        } else {
          await addProduct(updatedData).unwrap();
          showSuccessToast("Product added successfully");
        }
        setModalOpen(false);
      } catch (err) {
        showToastOnce(
          err?.data?.message ||
            (edit ? "Failed to update product" : "Failed to add product")
        );
      } finally {
        setSubmitting(false);
      }
    },
  });
  console.log(formik.values.description, formik.errors.description);
  const [flavourInputs, setFlavourInputs] = useState([
    createInitialFlavourInput(),
  ]);

  console.log(flavourInputs, "flavourInputs");

  const addFlavour = () => {
    if (formik.values.flavours.length >= 20) {
      showToastOnce("You can add up to 20 flavours/colors only");
      return;
    }
    formik.setFieldValue("flavours", [
      ...formik.values.flavours,
      createInitialFlavour(),
    ]);
    setFlavourInputs([...flavourInputs, createInitialFlavourInput()]);
  };
  const removeFlavour = (idx) => {
    const newFlavours = formik.values.flavours.filter((_, i) => i !== idx);
    formik.setFieldValue("flavours", newFlavours);
    setFlavourInputs(flavourInputs.filter((_, i) => i !== idx));
  };
  // Add Size
  const addSize = (flavourIdx) => {
    if (formik.values.flavours[flavourIdx].sizes.length >= 20) {
      showToastOnce("You can add up to 20 sizes only");
      return;
    }
    const updatedFlavours = formik.values.flavours.map((flavour, idx) =>
      idx === flavourIdx
        ? {
            ...flavour,
            sizes: [...flavour.sizes, createInitialSize()],
          }
        : flavour
    );
    formik.setFieldValue("flavours", updatedFlavours);
    setFlavourInputs((prev) =>
      prev.map((input, idx) =>
        idx === flavourIdx
          ? {
              ...input,
              sizeInputs: [
                ...input.sizeInputs,
                { ...createInitialSize(), quantity: 100 },
              ],
            }
          : input
      )
    );
  };

  // Remove Size
  const removeSize = (flavourIdx, sizeIdx) => {
    const updatedFlavours = formik.values.flavours.map((flavour, idx) =>
      idx === flavourIdx
        ? {
            ...flavour,
            sizes: flavour.sizes.filter((_, i) => i !== sizeIdx),
          }
        : flavour
    );
    formik.setFieldValue("flavours", updatedFlavours);

    setFlavourInputs((prev) =>
      prev.map((input, idx) =>
        idx === flavourIdx
          ? {
              ...input,
              sizeInputs: input.sizeInputs.filter((_, i) => i !== sizeIdx),
            }
          : input
      )
    );
  };
  // Add Tag
  const addTag = (flavourIdx) => {
    const tag = flavourInputs[flavourIdx].tagInput.trim();
    if (!tag) return;

    const updatedFlavours = addToArray(
      formik.values.flavours,
      flavourIdx,
      "tags",
      tag
    );
    formik.setFieldValue("flavours", updatedFlavours);

    setFlavourInputs((prev) =>
      prev.map((input, idx) =>
        idx === flavourIdx ? { ...input, tagInput: "" } : input
      )
    );
  };
  // Add Ingredient
  const addIngredient = (flavourIdx) => {
    const ingredient = flavourInputs[flavourIdx].ingredientInput.trim();
    if (!ingredient) return;

    // Prevent duplicate
    const currentIngredients =
      formik.values.flavours[flavourIdx].ingredients || [];
    if (
      currentIngredients.some(
        (i) => i.toLowerCase() === ingredient.toLowerCase()
      )
    ) {
      showToastOnce("Ingredient already added");
      return;
    }
    const updatedFlavours = addToArray(
      formik.values.flavours,
      flavourIdx,
      "ingredients",
      ingredient
    );
    formik.setFieldValue("flavours", updatedFlavours);
    setFlavourInputs((prev) =>
      prev.map((input, idx) =>
        idx === flavourIdx ? { ...input, ingredientInput: "" } : input
      )
    );
  };
  const removeTag = (flavourIdx, tagIdx) => {
    const updatedFlavours = removeFromArray(
      formik.values.flavours,
      flavourIdx,
      "tags",
      tagIdx
    );
    formik.setFieldValue("flavours", updatedFlavours);
  };

  const removeIngredient = (flavourIdx, ingIdx) => {
    const updatedFlavours = removeFromArray(
      formik.values.flavours,
      flavourIdx,
      "ingredients",
      ingIdx
    );
    formik.setFieldValue("flavours", updatedFlavours);
  };
  const [activeTab, setActiveTab] = useState("product");
  return (
    <Modal
      show={modalOpen}
      enforceFocus={false}
      className={`${styles.ProductPop} transparentModal`}
      onHide={() => setModalOpen(false)}
      backdrop={true}
      keyboard={false}
      centered
      size="lg"
    >
      <Modal.Body className={`${styles.modalBody} position-relative`}>
        <div className={`${styles.modal_hd}`}>
          <h3 className="m-0 fw-bold themeBlue">{title}</h3>
          <button
            type="button"
            className="btn-close"
            style={styles.modelCloseBtn}
            aria-label="Close"
            onClick={() => setModalOpen(false)}
          />
        </div>
        <div className="mx-auto">
          <form onSubmit={formik.handleSubmit}>
            <Tabs
              id="product-flavour-tabs"
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-3"
            >
              <Tab eventKey="product" title="Product">
                <Row>
                  <Col lg="6" md="6" className="my-2">
                    <div className="py-2">
                      <label
                        className="form-label fw-sbold text-muted ps-2 m-0"
                        htmlFor="product-name"
                      >
                        Name <span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        id="product-name"
                        className="form-control"
                        placeholder="Enter product name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        maxLength={40}
                      />
                      {formik.touched.name && formik.errors.name && (
                        <div className="text-danger">{formik.errors.name}</div>
                      )}
                    </div>
                  </Col>
                  <Col lg="6" md="6" className="my-2">
                    <div className="py-2">
                      <label
                        className="form-label fw-sbold text-muted ps-2 m-0"
                        htmlFor="product-serving"
                      >
                        Serving<span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="serving"
                        id="product-serving"
                        className="form-control"
                        placeholder="Enter product serving"
                        value={formik.values.serving}
                        onChange={formik.handleChange}
                        maxLength={40}
                      />
                      {formik.touched.serving && formik.errors.serving && (
                        <div className="text-danger">
                          {formik.errors.serving}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col lg="6" md="6" className="my-2">
                    <div className="py-2">
                      <label
                        className="form-label fw-sbold text-muted ps-2 m-0"
                        htmlFor="product-category"
                      >
                        Category <span style={{ color: "red" }}>*</span>
                      </label>
                      <Select
                        name="category"
                        inputId="product-category"
                        options={
                          categoriesData?.data?.map((cat) => ({
                            value: cat._id,
                            label: cat.name,
                          })) || []
                        }
                        className="basic-multi-select"
                        classNamePrefix="select"
                        value={
                          categoriesData?.data
                            ?.map((cat) => ({
                              value: cat._id,
                              label: cat.name,
                            }))
                            .find(
                              (opt) => opt.value === formik.values.category
                            ) || null
                        }
                        onChange={(option) => {
                          if (
                            option &&
                            !categoriesData?.data?.some(
                              (cat) => cat._id === option.value
                            )
                          ) {
                            showToastOnce(
                              `${option.label} is no longer available.`
                            );
                            formik.setFieldValue("category", "");
                            setSelectedCategory(null);
                            return;
                          }
                          formik.setFieldValue(
                            "category",
                            option ? option.value : ""
                          );
                          setSelectedCategory(option);
                          formik.setFieldValue("subCategory", "");
                        }}
                        placeholder={
                          isCategoriesLoading ? "Loading..." : "Select category"
                        }
                      />
                      {formik.touched.category && formik.errors.category && (
                        <div className="text-danger">
                          {formik.errors.category}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col lg="6" md="6" className="my-2">
                    <div className="py-2">
                      <label
                        className="form-label fw-sbold text-muted ps-2 m-0"
                        htmlFor="product-subCategory"
                      >
                        Sub Category
                      </label>
                      <Select
                        name="subCategory"
                        inputId="product-subCategory"
                        options={
                          subCategoriesData?.data?.map((cat) => ({
                            value: cat._id,
                            label: cat.name,
                          })) || []
                        }
                        className="basic-multi-select"
                        classNamePrefix="select"
                        value={
                          subCategoriesData?.data
                            ?.map((cat) => ({
                              value: cat._id,
                              label: cat.name,
                            }))
                            .find(
                              (opt) => opt.value === formik.values.subCategory
                            ) || null
                        }
                        onChange={(option) => {
                          if (
                            option &&
                            !subCategoriesData?.data?.some(
                              (cat) => cat._id === option.value
                            )
                          ) {
                            showToastOnce(
                              `${option.label} is no longer available.`
                            );
                            formik.setFieldValue("subCategory", "");
                            return;
                          }
                          formik.setFieldValue(
                            "subCategory",
                            option ? option.value : ""
                          );
                        }}
                        placeholder={
                          isSubCategoriesLoading
                            ? "Loading..."
                            : "Select sub category"
                        }
                        isDisabled={!selectedCategory}
                      />
                    </div>
                  </Col>
                  <Col lg="12" className="my-2">
                    <div className="py-2">
                      <label
                        className="form-label fw-sbold text-muted ps-2 m-0"
                        htmlFor="product-description"
                      >
                        Description
                      </label>
                      <CKEditorNew
                        key={"shekhar"}
                        field="description"
                        value={formik.values.description}
                        setFieldValue={formik.setFieldValue}
                      />
                      {formik.touched.description &&
                        !formik.values.description.trim() && (
                          <div className="text-danger">
                            {"Desciption is required"}
                          </div>
                        )}
                    </div>
                  </Col>
                </Row>
                <div className="d-flex justify-content-end gap-2 mt-3">
                  <Button
                    className="fw-sbold d-flex align-items-center justify-content-center commonBtn w-70"
                    type="button"
                    onClick={async () => {
                      // if (
                      //   !formik.values.description ||
                      //   formik.values.description.trim() === ""
                      // ) {
                      //   showToastOnce("Description can't be empty");
                      //   // setSubmitting(false);
                      //   return;
                      // }
                      await formik.setTouched({
                        name: true,
                        category: true,
                        subCategory: true,
                        description: true,
                        serving: true,
                      });
                      const errors = await formik.validateForm();
                      if (!errors.name && !errors.category) {
                        setActiveTab("flavour");
                      }
                    }}
                  >
                    Next
                  </Button>
                </div>
              </Tab>

              <Tab eventKey="flavour" title="Other Info">
                <Row>
                  <Col lg="6" md="6" className="my-2">
                    <div className="py-2">
                      <label className="form-label fw-sbold text-muted ps-2 m-0">
                        Variation Type <span style={{ color: "red" }}>*</span>
                      </label>
                      <select
                        name="variationType"
                        className="form-control"
                        value={formik.values.variationType}
                        onChange={formik.handleChange}
                        // disabled={Boolean(edit)}
                      >
                        <option value={Variation_TYPE.FLAVOUR}>Flavour</option>
                        <option value={Variation_TYPE.COLOR}>Color</option>
                      </select>
                    </div>
                  </Col>
                </Row>
                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5>
                      {formik.values.variationType === Variation_TYPE.COLOR
                        ? "Colors"
                        : "Flavours"}
                    </h5>
                    <Button
                      className="fw-sbold d-flex align-items-center justify-content-center commonBtn w-70"
                      variant="success"
                      type="button"
                      onClick={addFlavour}
                      disabled={!formik.values.variationType}
                    >
                      Add{" "}
                      {formik.values.variationType === Variation_TYPE.COLOR
                        ? "Color"
                        : "Flavour"}
                    </Button>
                  </div>
                  {formik.values.flavours?.length &&
                    formik.values.flavours
                      ?.toReversed()
                      .map((flavour, ffIdx) => {
                        const fIdx = formik.values.flavours?.length - ffIdx - 1;
                        return (
                          <div key={fIdx} className="border rounded p-3 mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6>
                                {formik.values.variationType ===
                                Variation_TYPE.COLOR
                                  ? `Color ${fIdx + 1}`
                                  : `Flavour ${fIdx + 1}`}
                              </h6>
                              {formik.values.flavours.length > 1 && fIdx ? (
                                <Button
                                  className="fw-sbold d-flex align-items-center justify-content-center commonBtn w-10 GreyBtn"
                                  type="button"
                                  size="sm"
                                  onClick={() => removeFlavour(fIdx)}
                                >
                                  Remove
                                </Button>
                              ) : null}
                            </div>
                            <Row>
                              <Col md="6" className="my-2">
                                <label
                                  className="form-label"
                                  htmlFor={`flavour-name-${fIdx}`}
                                >
                                  {formik.values.variationType ===
                                  Variation_TYPE.COLOR
                                    ? "Color Name"
                                    : "Flavour Name"}{" "}
                                  <span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                  type="text"
                                  className="form-control"
                                  id={`flavour-name-${fIdx}`}
                                  value={flavour.name}
                                  onChange={(e) => {
                                    const updatedFlavours =
                                      formik.values.flavours.map((item, i) =>
                                        i === fIdx
                                          ? { ...item, name: e.target.value }
                                          : item
                                      );
                                    formik.setFieldValue(
                                      "flavours",
                                      updatedFlavours
                                    );
                                  }}
                                  onBlur={() => {
                                    formik.setFieldTouched(
                                      `flavours[${fIdx}].name`,
                                      true
                                    );
                                  }}
                                  maxLength={40}
                                />
                                {formik.touched.flavours &&
                                formik.errors.flavours &&
                                formik.errors.flavours[fIdx] &&
                                formik.errors.flavours[fIdx].name ? (
                                  <div className="text-danger">
                                    {formik.errors.flavours[fIdx].name}
                                  </div>
                                ) : null}
                              </Col>
                              <Col md="6" className="my-2">
                                <label
                                  className="form-label"
                                  htmlFor={`flavour-images-${fIdx}`}
                                >
                                  {formik.values.variationType ===
                                  Variation_TYPE.COLOR
                                    ? "Color Images"
                                    : "Flavour Images"}
                                </label>
                                <ProductVariationDropzone
                                  field={`flavours[${fIdx}].images`}
                                  inputId={`flavour-images-${fIdx}`}
                                  setFieldValue={(fieldName, urls) => {
                                    const updatedFlavours =
                                      formik.values.flavours.map((item, i) =>
                                        i === fIdx
                                          ? {
                                              ...item,
                                              images: [
                                                ...(item.images || []),
                                                ...(urls || []),
                                              ],
                                            }
                                          : item
                                      );
                                    formik.setFieldValue(
                                      "flavours",
                                      updatedFlavours
                                    );
                                  }}
                                  onBlur={() => {
                                    formik.setFieldTouched(
                                      `flavours[${fIdx}].images`,
                                      true
                                    );
                                  }}
                                >
                                  <div
                                    className="dropzone-message"
                                    style={{
                                      cursor: "pointer",
                                      background: "#e6f0fa",
                                      border: "2px dashed #90caf9",
                                      borderRadius: "8px",
                                      minHeight: 120,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      padding: 24,
                                      marginBottom: 8,
                                    }}
                                  >
                                    <p
                                      style={{
                                        margin: 0,
                                        color: "#1976d2",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Drag and drop images here, or click to
                                      select files
                                    </p>
                                  </div>
                                </ProductVariationDropzone>
                                {formik.touched.flavours &&
                                  formik.errors.flavours &&
                                  formik.errors.flavours[fIdx] &&
                                  formik.errors.flavours[fIdx].images && (
                                    <div className="text-danger">
                                      {formik.errors.flavours[fIdx].images}
                                    </div>
                                  )}
                                {(flavour.images || []).length > 0 && (
                                  <div className="mt-3">
                                    <div className="d-flex flex-wrap gap-2">
                                      {(flavour.images || []).map(
                                        (image, i) => (
                                          <div
                                            key={i}
                                            className="position-relative"
                                          >
                                            <img
                                              src={image}
                                              alt={
                                                formik.values.variationType ===
                                                Variation_TYPE.COLOR
                                                  ? "Color"
                                                  : "Flavour"
                                              }
                                              style={{
                                                width: 80,
                                                height: 80,
                                                objectFit: "cover",
                                                borderRadius: "4px",
                                                border: "1px solid #ddd",
                                              }}
                                            />
                                            <button
                                              type="button"
                                              className="btn-close position-absolute top-0 end-0 bg-white"
                                              style={{
                                                fontSize: "10px",
                                                padding: "2px",
                                              }}
                                              onClick={() => {
                                                const updatedFlavours =
                                                  formik.values.flavours.map(
                                                    (item, idx) =>
                                                      idx === fIdx
                                                        ? {
                                                            ...item,
                                                            images:
                                                              item.images?.filter(
                                                                (_, imgIdx) =>
                                                                  imgIdx !== i
                                                              ) || [],
                                                          }
                                                        : item
                                                  );
                                                formik.setFieldValue(
                                                  "flavours",
                                                  updatedFlavours
                                                );
                                              }}
                                            />
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </div>
                                )}
                              </Col>
                            </Row>
                            {formik.values.variationType ==
                              Variation_TYPE.FLAVOUR && (
                              <Row>
                                <label className="form-label">
                                  Nutritional Information
                                </label>

                                <Col md="3" className="my-2">
                                  <label
                                    className="form-label"
                                    htmlFor={`flavour-calories-${fIdx}`}
                                  >
                                    Calories
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    id={`flavour-calories-${fIdx}`}
                                    maxLength={10}
                                    value={
                                      flavour.nutritionalInformation
                                        ?.calories || ""
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value.replace(
                                        /[^0-9]/g,
                                        ""
                                      );
                                      const updatedFlavours =
                                        updateNutritionalField(
                                          formik.values.flavours,
                                          fIdx,
                                          "calories",
                                          value
                                        );
                                      formik.setFieldValue(
                                        "flavours",
                                        updatedFlavours
                                      );
                                    }}
                                    onPaste={(e) => {
                                      const paste =
                                        e.clipboardData.getData("text");
                                      if (!/^\d+$/.test(paste)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    onKeyPress={(e) => {
                                      if (!/[0-9]/.test(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    disabled={
                                      formik.values.variationType ===
                                      Variation_TYPE.COLOR
                                    }
                                  />
                                </Col>
                                <Col md="3" className="my-2">
                                  <label
                                    className="form-label"
                                    htmlFor={`flavour-carbohydrates-${fIdx}`}
                                  >
                                    Carbohydrates
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    id={`flavour-carbohydrates-${fIdx}`}
                                    maxLength={10}
                                    value={
                                      flavour.nutritionalInformation
                                        ?.carbohydrates || ""
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value.replace(
                                        /[^0-9]/g,
                                        ""
                                      );
                                      const updatedFlavours =
                                        updateNutritionalField(
                                          formik.values.flavours,
                                          fIdx,
                                          "carbohydrates",
                                          value
                                        );
                                      formik.setFieldValue(
                                        "flavours",
                                        updatedFlavours
                                      );
                                    }}
                                    onPaste={(e) => {
                                      const paste =
                                        e.clipboardData.getData("text");
                                      if (!/^\d+$/.test(paste)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    onKeyPress={(e) => {
                                      if (!/[0-9]/.test(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    disabled={
                                      formik.values.variationType ===
                                      Variation_TYPE.COLOR
                                    }
                                  />
                                </Col>
                                <Col md="3" className="my-2">
                                  <label
                                    className="form-label"
                                    htmlFor={`flavour-proteins-${fIdx}`}
                                  >
                                    Proteins
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    id={`flavour-proteins-${fIdx}`}
                                    maxLength={10}
                                    value={
                                      flavour.nutritionalInformation
                                        ?.proteins || ""
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value.replace(
                                        /[^0-9]/g,
                                        ""
                                      );
                                      const updatedFlavours =
                                        updateNutritionalField(
                                          formik.values.flavours,
                                          fIdx,
                                          "proteins",
                                          value
                                        );
                                      formik.setFieldValue(
                                        "flavours",
                                        updatedFlavours
                                      );
                                    }}
                                    onPaste={(e) => {
                                      const paste =
                                        e.clipboardData.getData("text");
                                      if (!/^\d+$/.test(paste)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    onKeyPress={(e) => {
                                      if (!/[0-9]/.test(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    disabled={
                                      formik.values.variationType ===
                                      Variation_TYPE.COLOR
                                    }
                                  />
                                </Col>
                                <Col md="3" className="my-2">
                                  <label
                                    className="form-label"
                                    htmlFor={`flavour-fats-${fIdx}`}
                                  >
                                    Fats
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    id={`flavour-fats-${fIdx}`}
                                    maxLength={10}
                                    value={
                                      flavour.nutritionalInformation?.fats || ""
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value.replace(
                                        /[^0-9]/g,
                                        ""
                                      );
                                      const updatedFlavours =
                                        updateNutritionalField(
                                          formik.values.flavours,
                                          fIdx,
                                          "fats",
                                          value
                                        );
                                      formik.setFieldValue(
                                        "flavours",
                                        updatedFlavours
                                      );
                                    }}
                                    onPaste={(e) => {
                                      const paste =
                                        e.clipboardData.getData("text");
                                      if (!/^\d+$/.test(paste)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    onKeyPress={(e) => {
                                      if (!/[0-9]/.test(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    disabled={
                                      formik.values.variationType ===
                                      Variation_TYPE.COLOR
                                    }
                                  />
                                </Col>
                              </Row>
                            )}
                            <Row>
                              <Col md="12" className="my-2">
                                <label
                                  className="form-label"
                                  htmlFor={`flavour-tag-input-${fIdx}`}
                                >
                                  Tags
                                </label>
                                <div className="d-flex gap-2 mb-2">
                                  <input
                                    type="text"
                                    id={`flavour-tag-input-${fIdx}`}
                                    value={flavourInputs[fIdx]?.tagInput || ""}
                                    onChange={(e) => {
                                      setFlavourInputs((prev) =>
                                        prev.map((input, idx) => {
                                          return idx === fIdx
                                            ? {
                                                ...input,
                                                tagInput: e.target.value,
                                              }
                                            : input;
                                        })
                                      );
                                    }}
                                    className="form-control"
                                    placeholder={
                                      formik.values.variationType ===
                                      Variation_TYPE.COLOR
                                        ? "Add a color tag"
                                        : "Add a flavour tag"
                                    }
                                  />
                                  <Button
                                    variant="secondary"
                                    type="button"
                                    onClick={() => addTag(fIdx)}
                                  >
                                    Add
                                  </Button>
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                  {(flavour.tags || []).map((tag, i) => (
                                    <div
                                      key={i}
                                      className="badge bg-primary d-flex align-items-center gap-2"
                                    >
                                      {tag}
                                      <button
                                        type="button"
                                        className=" btn-close text-white "
                                        onClick={() => removeTag(fIdx, i)}
                                      ></button>
                                    </div>
                                  ))}
                                </div>
                              </Col>
                            </Row>
                            <Row>
                              <Col md="12" className="my-2">
                                <label
                                  className="form-label"
                                  htmlFor={`flavour-subtext-${fIdx}`}
                                >
                                  Sub Text
                                </label>
                                <textarea
                                  className="form-control"
                                  id={`flavour-subtext-${fIdx}`}
                                  value={flavour.subText}
                                  onChange={(e) => {
                                    const updatedFlavours =
                                      formik.values.flavours.map((item, i) =>
                                        i === fIdx
                                          ? {
                                              ...item,
                                              subText: e.target.value,
                                            }
                                          : item
                                      );
                                    formik.setFieldValue(
                                      "flavours",
                                      updatedFlavours
                                    );
                                  }}
                                />
                              </Col>
                            </Row>
                            <Row>
                              {formik.values.variationType ==
                                Variation_TYPE.FLAVOUR && (
                                <Col md="12" className="my-2">
                                  <label
                                    className="form-label"
                                    htmlFor={`flavour-ingredient-input-${fIdx}`}
                                  >
                                    Ingredients
                                  </label>
                                  <div className="d-flex gap-2 mb-2">
                                    <input
                                      type="text"
                                      id={`flavour-ingredient-input-${fIdx}`}
                                      value={
                                        flavourInputs[fIdx]?.ingredientInput ||
                                        ""
                                      }
                                      onChange={(e) => {
                                        setFlavourInputs((prev) =>
                                          prev.map((input, idx) =>
                                            idx === fIdx
                                              ? {
                                                  ...input,
                                                  ingredientInput:
                                                    e.target.value,
                                                }
                                              : input
                                          )
                                        );
                                      }}
                                      className="form-control"
                                      placeholder="Add an ingredient"
                                      disabled={
                                        formik.values.variationType ===
                                        Variation_TYPE.COLOR
                                      }
                                    />
                                    <Button
                                      variant="secondary"
                                      type="button"
                                      onClick={() => addIngredient(fIdx)}
                                      disabled={
                                        formik.values.variationType ===
                                        Variation_TYPE.COLOR
                                      }
                                    >
                                      Add
                                    </Button>
                                  </div>
                                  <div className="d-flex flex-wrap gap-2">
                                    {(flavour.ingredients || []).map(
                                      (ingredient, i) => (
                                        <div
                                          key={i}
                                          className="badge bg-primary d-flex align-items-center gap-2"
                                        >
                                          {ingredient}
                                          <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() =>
                                              removeIngredient(fIdx, i)
                                            }
                                            disabled={
                                              formik.values.variationType ===
                                              Variation_TYPE.COLOR
                                            }
                                          />
                                        </div>
                                      )
                                    )}
                                  </div>
                                </Col>
                              )}
                            </Row>
                            {/* Sizes for this Flavour/Color */}
                            <div className="mt-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6>Sizes</h6>
                                {flavour.sizes.length < 10 && (
                                  <Button
                                    className="fw-sbold d-flex align-items-center justify-content-center commonBtn w-20"
                                    type="button"
                                    onClick={() => addSize(fIdx)}
                                  >
                                    Add Size
                                  </Button>
                                )}
                              </div>
                              {flavour.sizes.map((size, sIdx) => (
                                <div
                                  key={sIdx}
                                  className="border rounded p-2 mb-2"
                                >
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span>Size {sIdx + 1}</span>
                                    {flavour.sizes.length > 1 ? (
                                      <Button
                                        className="fw-sbold d-flex align-items-center justify-content-center commonBtn w-10 GreyBtn"
                                        type="button"
                                        size="sm"
                                        onClick={() => removeSize(fIdx, sIdx)}
                                      >
                                        Remove
                                      </Button>
                                    ) : null}
                                  </div>
                                  <Row>
                                    <Col md="4" className="my-2">
                                      <label
                                        className="form-label"
                                        htmlFor={`size-name-${fIdx}-${sIdx}`}
                                      >
                                        Size Name{" "}
                                        <span style={{ color: "red" }}>*</span>
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        id={`size-name-${fIdx}-${sIdx}`}
                                        value={size.name}
                                        onChange={(e) => {
                                          const updatedFlavours =
                                            formik.values.flavours.map(
                                              (item, i) =>
                                                i === fIdx
                                                  ? {
                                                      ...item,
                                                      sizes: item.sizes.map(
                                                        (sz, idx) =>
                                                          idx === sIdx
                                                            ? {
                                                                ...sz,
                                                                name: e.target
                                                                  .value,
                                                              }
                                                            : sz
                                                      ),
                                                    }
                                                  : item
                                            );
                                          formik.setFieldValue(
                                            "flavours",
                                            updatedFlavours
                                          );
                                        }}
                                        onBlur={() => {
                                          formik.setFieldTouched(
                                            `flavours[${fIdx}].sizes[${sIdx}].name`,
                                            true
                                          );
                                        }}
                                        maxLength={40}
                                      />
                                      {formik.touched.flavours &&
                                      formik.errors.flavours &&
                                      formik.errors.flavours[fIdx] &&
                                      formik.errors.flavours[fIdx].sizes &&
                                      formik.errors.flavours[fIdx].sizes[
                                        sIdx
                                      ] &&
                                      formik.errors.flavours[fIdx].sizes[sIdx]
                                        .name ? (
                                        <div className="text-danger">
                                          {
                                            formik.errors.flavours[fIdx].sizes[
                                              sIdx
                                            ].name
                                          }
                                        </div>
                                      ) : null}
                                    </Col>
                                    <Col md="2" className="my-2">
                                      <label
                                        className="form-label"
                                        htmlFor={`size-quantity-${fIdx}-${sIdx}`}
                                      >
                                        Quantity{" "}
                                        <span style={{ color: "red" }}>*</span>
                                      </label>
                                      <input
                                        type="text"
                                        id={`size-quantity-${fIdx}-${sIdx}`}
                                        step="1"
                                        className="form-control"
                                        inputMode="numeric"
                                        maxLength={10}
                                        pattern="\d*"
                                        value={size.quantity}
                                        onPaste={(e) => {
                                          const paste =
                                            e.clipboardData.getData("text");
                                          if (!/^\d+$/.test(paste)) {
                                            e.preventDefault();
                                          }
                                        }}
                                        onChange={(e) => {
                                          const input = e.target.value;
                                          if (/^\d*$/.test(input)) {
                                            const updatedFlavours =
                                              formik.values.flavours.map(
                                                (item, i) =>
                                                  i === fIdx
                                                    ? {
                                                        ...item,
                                                        sizes: item.sizes.map(
                                                          (sz, idx) =>
                                                            idx === sIdx
                                                              ? {
                                                                  ...sz,
                                                                  quantity:
                                                                    input,
                                                                }
                                                              : sz
                                                        ),
                                                      }
                                                    : item
                                              );
                                            formik.setFieldValue(
                                              "flavours",
                                              updatedFlavours
                                            );
                                          }
                                        }}
                                      />
                                      {formik.touched.flavours &&
                                        formik.errors.flavours &&
                                        formik.errors.flavours[fIdx] &&
                                        formik.errors.flavours[fIdx].sizes &&
                                        formik.errors.flavours[fIdx].sizes[
                                          sIdx
                                        ] &&
                                        formik.errors.flavours[fIdx].sizes[sIdx]
                                          .quantity && (
                                          <div className="text-danger">
                                            {
                                              formik.errors.flavours[fIdx]
                                                .sizes[sIdx].quantity
                                            }
                                          </div>
                                        )}
                                    </Col>

                                    <Col md="3" className="my-2">
                                      <label
                                        className="form-label"
                                        htmlFor={`size-price-${fIdx}-${sIdx}`}
                                      >
                                        Price
                                        <span style={{ color: "red" }}>*</span>
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        id={`size-price-${fIdx}-${sIdx}`}
                                        inputMode="decimal"
                                        maxLength={11}
                                        pattern="^\d{0,8}(\.\d{0,2})?$"
                                        placeholder="0.00"
                                        value={size.price || ""}
                                        onPaste={(e) => {
                                          const paste =
                                            e.clipboardData.getData("text");
                                          if (
                                            !/^\d{0,8}(\.\d{0,2})?$/.test(paste)
                                          ) {
                                            e.preventDefault();
                                          }
                                        }}
                                        onChange={(e) => {
                                          const input = e.target.value;
                                          const regex = /^\d{0,8}(\.\d{0,2})?$/;
                                          if (
                                            input === "" ||
                                            regex.test(input)
                                          ) {
                                            const updatedFlavours =
                                              formik.values.flavours.map(
                                                (item, i) =>
                                                  i === fIdx
                                                    ? {
                                                        ...item,
                                                        sizes: item.sizes.map(
                                                          (sz, idx) =>
                                                            idx === sIdx
                                                              ? {
                                                                  ...sz,
                                                                  price: input,
                                                                }
                                                              : sz
                                                        ),
                                                      }
                                                    : item
                                              );
                                            formik.setFieldValue(
                                              "flavours",
                                              updatedFlavours
                                            );
                                          }
                                        }}
                                        onBlur={() => {
                                          formik.setFieldTouched(
                                            `flavours[${fIdx}].sizes[${sIdx}].price`,
                                            true
                                          );
                                        }}
                                      />
                                      {formik.touched.flavours &&
                                        formik.errors.flavours &&
                                        formik.errors.flavours[fIdx] &&
                                        formik.errors.flavours[fIdx].sizes &&
                                        formik.errors.flavours[fIdx].sizes[
                                          sIdx
                                        ] &&
                                        formik.errors.flavours[fIdx].sizes[sIdx]
                                          .price && (
                                          <div className="text-danger">
                                            {
                                              formik.errors.flavours[fIdx]
                                                .sizes[sIdx].price
                                            }
                                          </div>
                                        )}
                                    </Col>

                                    <Col md="3" className="my-2">
                                      <label
                                        className="form-label"
                                        htmlFor={`size-discountedPrice-${fIdx}-${sIdx}`}
                                      >
                                        Discounted Price
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        id={`size-discountedPrice-${fIdx}-${sIdx}`}
                                        inputMode="numeric"
                                        maxLength={10}
                                        pattern="\d*"
                                        onPaste={(e) => {
                                          const paste =
                                            e.clipboardData.getData("text");
                                          if (!/^\d+$/.test(paste)) {
                                            e.preventDefault();
                                          }
                                        }}
                                        value={size.discountedPrice || ""}
                                        onChange={(e) => {
                                          const input = e.target.value;
                                          if (/^\d*$/.test(input)) {
                                            const updatedFlavours =
                                              formik.values.flavours.map(
                                                (item, i) =>
                                                  i === fIdx
                                                    ? {
                                                        ...item,
                                                        sizes: item.sizes.map(
                                                          (sz, idx) =>
                                                            idx === sIdx
                                                              ? {
                                                                  ...sz,
                                                                  discountedPrice:
                                                                    input,
                                                                }
                                                              : sz
                                                        ),
                                                      }
                                                    : item
                                              );
                                            formik.setFieldValue(
                                              "flavours",
                                              updatedFlavours
                                            );
                                          }
                                        }}
                                      />
                                      {formik.touched.flavours &&
                                        formik.errors.flavours &&
                                        formik.errors.flavours[fIdx] &&
                                        formik.errors.flavours[fIdx].sizes &&
                                        formik.errors.flavours[fIdx].sizes[
                                          sIdx
                                        ] &&
                                        formik.errors.flavours[fIdx].sizes[sIdx]
                                          .discountedPrice && (
                                          <div className="text-danger">
                                            {
                                              formik.errors.flavours[fIdx]
                                                .sizes[sIdx].discountedPrice
                                            }
                                          </div>
                                        )}
                                    </Col>
                                  </Row>
                                  <Row>
                                    <Col md="12" className="my-2">
                                      <label
                                        className="form-label"
                                        htmlFor={`size-images-${fIdx}-${sIdx}`}
                                      >
                                        Size Images
                                        <span style={{ color: "red" }}>*</span>
                                      </label>
                                      <ProductVariationDropzone
                                        field={`flavours[${fIdx}].sizes[${sIdx}].images`}
                                        inputId={`size-images-${fIdx}-${sIdx}`}
                                        setFieldValue={(fieldName, urls) => {
                                          const updatedFlavours =
                                            formik.values.flavours.map(
                                              (item, i) =>
                                                i === fIdx
                                                  ? {
                                                      ...item,
                                                      sizes: item.sizes.map(
                                                        (sz, idx) =>
                                                          idx === sIdx
                                                            ? {
                                                                ...sz,
                                                                images: [
                                                                  ...(sz.images ||
                                                                    []),
                                                                  ...(urls ||
                                                                    []),
                                                                ],
                                                              }
                                                            : sz
                                                      ),
                                                    }
                                                  : item
                                            );
                                          formik.setFieldValue(
                                            "flavours",
                                            updatedFlavours
                                          );
                                        }}
                                        onBlur={() => {
                                          formik.setFieldTouched(
                                            `flavours[${fIdx}].sizes[${sIdx}].images`,
                                            true
                                          );
                                        }}
                                      >
                                        <div
                                          className="dropzone-message"
                                          style={{
                                            cursor: "pointer",
                                            background: "#e6f0fa",
                                            border: "2px dashed #90caf9",
                                            borderRadius: "8px",
                                            minHeight: 120,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: 24,
                                            marginBottom: 8,
                                          }}
                                        >
                                          <p
                                            style={{
                                              margin: 0,
                                              color: "#1976d2",
                                              fontWeight: 500,
                                            }}
                                          >
                                            Drag and drop images here, or click
                                            to select files
                                          </p>
                                        </div>
                                      </ProductVariationDropzone>
                                      {formik.touched.flavours &&
                                        formik.errors.flavours &&
                                        formik.errors.flavours[fIdx] &&
                                        formik.errors.flavours[fIdx].sizes &&
                                        formik.errors.flavours[fIdx].sizes[
                                          sIdx
                                        ] &&
                                        formik.errors.flavours[fIdx].sizes[sIdx]
                                          .images && (
                                          <div className="text-danger">
                                            {
                                              formik.errors.flavours[fIdx]
                                                .sizes[sIdx].images
                                            }
                                          </div>
                                        )}
                                      {(size.images || []).length > 0 && (
                                        <div className="mt-3">
                                          <label className="form-label mb-2">
                                            Size Images:
                                          </label>
                                          <div className="d-flex flex-wrap gap-2">
                                            {(size.images || []).map(
                                              (image, i) => (
                                                <div
                                                  key={i}
                                                  className="position-relative"
                                                >
                                                  <img
                                                    src={image}
                                                    alt="Size"
                                                    style={{
                                                      width: 60,
                                                      height: 60,
                                                      objectFit: "cover",
                                                      borderRadius: "4px",
                                                      border: "1px solid #ddd",
                                                    }}
                                                  />
                                                  <button
                                                    type="button"
                                                    className="btn-close position-absolute top-0 end-0 bg-white"
                                                    style={{
                                                      fontSize: "10px",
                                                      padding: "2px",
                                                    }}
                                                    onClick={() => {
                                                      const updatedFlavours =
                                                        formik.values.flavours.map(
                                                          (item, idx) =>
                                                            idx === fIdx
                                                              ? {
                                                                  ...item,
                                                                  sizes:
                                                                    item.sizes.map(
                                                                      (
                                                                        sz,
                                                                        sizeIdx
                                                                      ) =>
                                                                        sizeIdx ===
                                                                        sIdx
                                                                          ? {
                                                                              ...sz,
                                                                              images:
                                                                                sz.images?.filter(
                                                                                  (
                                                                                    _,
                                                                                    imgIdx
                                                                                  ) =>
                                                                                    imgIdx !==
                                                                                    i
                                                                                ) ||
                                                                                [],
                                                                            }
                                                                          : sz
                                                                    ),
                                                                }
                                                              : item
                                                        );
                                                      formik.setFieldValue(
                                                        "flavours",
                                                        updatedFlavours
                                                      );
                                                    }}
                                                  />
                                                </div>
                                              )
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </Col>
                                  </Row>
                                </div>
                              ))}
                            </div>
                            <Row>
                              <Col md="6" className="my-2">
                                <label
                                  className="form-label"
                                  htmlFor={`flavour-stockStatus-${fIdx}`}
                                >
                                  Stock Status
                                </label>
                                <select
                                  className="form-control"
                                  id={`flavour-stockStatus-${fIdx}`}
                                  value={flavour.stockStatus || ""}
                                  onChange={(e) => {
                                    const updatedFlavours =
                                      formik.values.flavours.map((item, i) =>
                                        i === fIdx
                                          ? {
                                              ...item,
                                              stockStatus: e.target.value,
                                            }
                                          : item
                                      );
                                    formik.setFieldValue(
                                      "flavours",
                                      updatedFlavours
                                    );
                                  }}
                                >
                                  <option value="">Select</option>
                                  <option value={1}>In Stock</option>
                                  <option value={2}>Out of Stock</option>
                                </select>
                              </Col>
                              {formik.values.variationType ==
                                Variation_TYPE.FLAVOUR && (
                                <Col md="6" className="my-2">
                                  <label
                                    className="form-label"
                                    htmlFor={`flavour-dietaryPreferences-${fIdx}`}
                                  >
                                    Dietary Preferences
                                  </label>
                                  <Select
                                    isMulti
                                    name={`flavours[${fIdx}].dietaryPreferences`}
                                    inputId={`flavour-dietaryPreferences-${fIdx}`}
                                    options={
                                      dietaryData?.data?.map((item) => ({
                                        value: item._id,
                                        label: item.name,
                                      })) || []
                                    }
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    styles={{
                                      clearIndicator: (base) => ({
                                        ...base,
                                        cursor: "pointer",
                                      }),
                                    }}
                                    value={(flavour.dietaryPreferences || [])
                                      .map((id) =>
                                        dietaryData?.data
                                          ?.map((item) => ({
                                            value: item._id,
                                            label: item.name,
                                          }))
                                          .find((opt) => opt.value === id)
                                      )
                                      .filter(Boolean)}
                                    onChange={(selectedOptions) => {
                                      const updatedFlavours =
                                        formik.values.flavours.map((item, i) =>
                                          i === fIdx
                                            ? {
                                                ...item,
                                                dietaryPreferences:
                                                  selectedOptions
                                                    ? selectedOptions.map(
                                                        (opt) => opt.value
                                                      )
                                                    : [],
                                              }
                                            : item
                                        );
                                      formik.setFieldValue(
                                        "flavours",
                                        updatedFlavours
                                      );
                                    }}
                                    placeholder={
                                      isDietaryLoading
                                        ? "Loading..."
                                        : "Select dietary preferences"
                                    }
                                    isDisabled={
                                      formik.values.variationType ===
                                      Variation_TYPE.COLOR
                                    }
                                  />
                                  {/* Error for deleted dietary preferences (add mode only) */}
                                  {!edit &&
                                    Array.isArray(flavour.dietaryPreferences) &&
                                    Array.isArray(dietaryData?.data) &&
                                    flavour.dietaryPreferences
                                      .filter(
                                        (id) =>
                                          !dietaryData.data.some(
                                            (item) => item._id === id
                                          )
                                      )
                                      .map((id) => {
                                        let name = id;
                                        const found = dietaryData.data.find(
                                          (d) => d._id === id
                                        );
                                        if (found) name = found.name;
                                        return (
                                          <div
                                            key={id}
                                            className="text-danger mt-1"
                                          >
                                            Dietary Preferences is no longer
                                            available.
                                          </div>
                                        );
                                      })}
                                </Col>
                              )}
                            </Row>
                            {formik.values.variationType ==
                              Variation_TYPE.FLAVOUR && (
                              <Row className="allergen_info">
                                <Col md="12" className="my-2">
                                  <label
                                    className="form-label"
                                    htmlFor={`flavour-allergenInformation-${fIdx}`}
                                  >
                                    Allergen Information
                                  </label>
                                  <CKEditorNew
                                    field={`flavours[${fIdx}].allergenInformation`}
                                    inputId={`flavour-allergenInformation-${fIdx}`}
                                    value={flavour.allergenInformation}
                                    setFieldValue={(field, value) => {
                                      const updatedFlavours =
                                        formik.values.flavours.map((item, i) =>
                                          i === fIdx
                                            ? {
                                                ...item,
                                                allergenInformation: value,
                                              }
                                            : item
                                        );
                                      formik.setFieldValue(
                                        "flavours",
                                        updatedFlavours
                                      );
                                    }}
                                    disabled={
                                      formik.values.variationType ===
                                      Variation_TYPE.COLOR
                                    }
                                  />
                                </Col>
                              </Row>
                            )}
                          </div>
                        );
                      })}
                </div>
                <div className="d-flex justify-content-end gap-2 mt-3">
                  <Button
                    className="fw-sbold d-flex align-items-center justify-content-center commonBtn GreyBtn"
                    onClick={() => setModalOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="fw-sbold d-flex align-items-center justify-content-center commonBtn w-70"
                    type="submit"
                    disabled={
                      formik.isSubmitting || (edit ? isupdating : isAdding)
                    }
                  >
                    {isAdding || isupdating
                      ? "Saving..."
                      : edit
                      ? "Update"
                      : "Add"}
                  </Button>
                </div>
              </Tab>
            </Tabs>
          </form>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ProductPop;
